import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, Mail, ArrowRight, Recycle, ShieldCheck, Sparkles, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import AnimatedProgressBar from "@/components/AnimatedProgressBar";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import TawkToWidget from "@/components/TawkToWidget";

const PHONE = "+33 6 44 66 00 33";
const EMAIL = "hello@mydoritech.com";

/**
 * Typography rules:
 * - Titles / headings → text-brand-navy (#18346C)
 * - Body, paragraphs, labels, captions, small text → text-black (#000000)
 * - Green (#5CB338) → accents only
 * - Blue (#1B408A) → buttons & links only
 */
export default function LandingPage() {
  return (
    <div
      className="relative min-h-screen overflow-hidden bg-[#F8F9FA] font-inter"
      data-testid="landing-page"
    >
      {/* Soft ambient blobs */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-brand-lightBlue/15 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -right-40 h-[480px] w-[480px] rounded-full bg-brand-green/15 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-[420px] w-[420px] rounded-full bg-brand-purple/10 blur-3xl" />

      {/* Subtle dotted grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(24,52,108,0.10) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
          maskImage: "radial-gradient(ellipse at center, black 50%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 50%, transparent 100%)",
        }}
      />

      {/* TOP BAR */}
      <header className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-10 pt-6 sm:pt-8 flex items-center justify-between gap-4">
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Logo size="lg" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex items-center gap-3 sm:gap-5"
        >
          <span className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-black font-semibold">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-green opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-green" />
            </span>
            En préparation
          </span>
          <Link
            to="/login"
            data-testid="landing-login-link"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-brand-blue text-white text-sm font-semibold hover:bg-brand-navy transition-colors"
          >
            Accès partenaires <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </motion.div>
      </header>

      {/* HERO */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-10 pt-14 sm:pt-24 lg:pt-28 pb-20 sm:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-navy/10 px-3.5 py-1.5 shadow-sm shadow-brand-navy/5 mb-8"
        >
          <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-green">
            ◆ MYDORITECH
          </span>
          <span className="text-[10px] tracking-[0.2em] uppercase text-black font-semibold">
            Reconditionné premium · Made in France
          </span>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="font-black tracking-tighter leading-[0.92] text-balance text-brand-navy"
              style={{ fontSize: "clamp(2.6rem, 7.5vw, 6rem)" }}
              data-testid="landing-headline"
            >
              <span>Notre site est en</span>
              <br />
              <span className="bg-gradient-to-r from-brand-blue via-brand-lightBlue to-brand-green bg-clip-text text-transparent">
                cours de construction,
              </span>
              <br />
              <span>nous revenons</span>{" "}
              <span className="relative inline-block">
                très bientôt
                <motion.span
                  className="absolute left-0 right-0 -bottom-1 h-[4px] rounded-full bg-brand-green"
                  initial={{ scaleX: 0, originX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 1, duration: 0.7, ease: "easeOut" }}
                />
              </span>
              <span>!</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="mt-8 max-w-2xl text-base sm:text-lg text-black font-roboto leading-relaxed"
              data-testid="landing-subheadline"
            >
              Ordinateurs, tablettes, smartphones et écrans reconditionnés —
              testés, garantis 12 mois et expédiés depuis la France. Une autre
              façon de consommer la tech : performante, responsable, accessible.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-10 flex flex-wrap gap-3"
            >
              <a
                href={`mailto:${EMAIL}`}
                data-testid="hero-email-cta"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-brand-blue text-white text-sm font-semibold hover:bg-brand-navy transition-colors"
              >
                Nous écrire <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href={`tel:${PHONE.replace(/\s/g, "")}`}
                data-testid="hero-phone-cta"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white text-black text-sm font-semibold border border-brand-navy/15 hover:border-brand-navy/40 hover:shadow-md transition-all"
              >
                <Phone className="h-4 w-4 text-brand-blue" /> {PHONE}
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="lg:col-span-4 lg:pl-8 lg:border-l lg:border-brand-navy/10"
          >
            <div className="rounded-2xl bg-white border border-brand-navy/10 p-6 shadow-xl shadow-brand-navy/5">
              <AnimatedProgressBar progress={72} />
              <div className="mt-7 grid grid-cols-3 gap-4">
                <Stat label="Catalogue" value="120+" />
                <Stat label="Garantie" value="12 mois" />
                <Stat label="Origine" value="France" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUE PILLARS */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-10 pb-20">
        <div className="grid sm:grid-cols-3 gap-4">
          <Pillar
            icon={<Recycle className="h-5 w-5" />}
            title="Éco-responsable"
            text="Chaque appareil reconditionné, c'est 80 kg de CO₂ évités."
            tone="green"
          />
          <Pillar
            icon={<ShieldCheck className="h-5 w-5" />}
            title="Garantie 12 mois"
            text="Diagnostic 50 points + SAV français."
            tone="blue"
          />
          <Pillar
            icon={<Sparkles className="h-5 w-5" />}
            title="Premium uniquement"
            text="Sélection grade A & B, marques pro."
            tone="navy"
          />
        </div>
      </section>

      {/* CONTACT BAND */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-10 pb-32">
        <div className="grid md:grid-cols-2 gap-4">
          <a
            href={`tel:${PHONE.replace(/\s/g, "")}`}
            data-testid="landing-phone-link"
            className="group flex items-center gap-5 rounded-2xl bg-white border border-brand-navy/10 p-6 sm:p-8 shadow-lg shadow-brand-navy/5 hover:shadow-xl hover:shadow-brand-navy/10 hover:border-brand-green/40 transition-all"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-green/10 text-brand-green">
              <Phone className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.25em] text-brand-green font-bold">
                Téléphone
              </div>
              <div className="font-black text-xl sm:text-2xl text-brand-navy tracking-tight mt-1 truncate">
                {PHONE}
              </div>
              <div className="text-xs text-black font-roboto mt-1">Lun – Ven · 9h-18h</div>
            </div>
            <ArrowRight className="ml-auto h-5 w-5 text-black/30 group-hover:text-brand-green group-hover:translate-x-1 transition-all" />
          </a>

          <a
            href={`mailto:${EMAIL}`}
            data-testid="landing-email-link"
            className="group flex items-center gap-5 rounded-2xl bg-white border border-brand-navy/10 p-6 sm:p-8 shadow-lg shadow-brand-navy/5 hover:shadow-xl hover:shadow-brand-navy/10 hover:border-brand-blue/40 transition-all"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
              <Mail className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.25em] text-brand-blue font-bold">
                E-mail
              </div>
              <div className="font-black text-xl sm:text-2xl text-brand-navy tracking-tight mt-1 break-all">
                {EMAIL}
              </div>
              <div className="text-xs text-black font-roboto mt-1">Réponse sous 24h</div>
            </div>
            <ArrowRight className="ml-auto h-5 w-5 text-black/30 group-hover:text-brand-blue group-hover:translate-x-1 transition-all" />
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative z-10 border-t border-brand-navy/10 bg-white/60 backdrop-blur-sm">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-black font-roboto">
          <div>© {new Date().getFullYear()} MYDORITECH SAS · Tous droits réservés</div>
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              data-testid="footer-login-link"
              className="text-brand-blue hover:text-brand-navy font-semibold transition"
            >
              Accès partenaires
            </Link>
            <span className="text-black/40">·</span>
            <span>hello@mydoritech.com</span>
          </div>
        </div>
      </footer>

      {/* Floating widgets */}
      <WhatsAppFloat />
      <TawkToWidget />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[9px] uppercase tracking-[0.25em] text-black font-semibold">
        {label}
      </span>
      <span className="font-black text-base sm:text-lg text-brand-navy tracking-tight leading-none">
        {value}
      </span>
    </div>
  );
}

function Pillar({ icon, title, text, tone = "green" }) {
  const tones = {
    green: "bg-brand-green/10 text-brand-green",
    blue: "bg-brand-blue/10 text-brand-blue",
    navy: "bg-brand-navy/10 text-brand-navy",
  };
  return (
    <div className="rounded-2xl bg-white border border-brand-navy/10 p-7 hover:shadow-lg hover:shadow-brand-navy/5 hover:border-brand-navy/20 transition-all">
      <div className={`inline-flex items-center justify-center h-11 w-11 rounded-xl ${tones[tone]}`}>
        {icon}
      </div>
      <div className="mt-5 text-[11px] uppercase tracking-[0.25em] font-bold text-brand-navy">
        {title}
      </div>
      <p className="mt-2 font-roboto text-black text-[15px] leading-relaxed">{text}</p>
    </div>
  );
}
